import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { money } from "@/lib/format";
import { TrendUp, CurrencyDollar, Gauge, PhoneCall, Clock, CheckCircle } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";

const CH = ["#EA580C", "#0F172A", "#3B82F6"];
const OUTCOME_COLOR = { resolved: "#10B981", escalated: "#EF4444", follow_up_needed: "#F59E0B", no_answer: "#94A3B8", other: "#CBD5E1" };
const fmtDur = (s) => `${Math.floor((s || 0) / 60)}:${String((s || 0) % 60).padStart(2, "0")}`;

export default function Analytics() {
  const { data } = useQuery({ queryKey: ["analytics"], queryFn: async () => (await api.get("/analytics")).data });
  if (!data) return <Page><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></Page>;

  return (
    <Page>
      <PageHeader title="Analytics" subtitle="Blended cross-channel ROI across email, chat & voice." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="rounded-lg border border-border bg-card p-5 fade-up"><div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">Total revenue</div><div className="font-heading text-[28px] font-semibold mt-2">{money(data.total_revenue)}</div><div className="text-xs text-muted-foreground mt-1">Attributed, all channels</div></div>
        <div className="rounded-lg border border-border bg-card p-5 fade-up" style={{ animationDelay: "60ms" }}><div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">Blended ROI</div><div className="font-heading text-[28px] font-semibold mt-2 text-success">{data.roi}×</div><div className="text-xs text-muted-foreground mt-1">Revenue per $ spent</div></div>
        <div className="rounded-lg border border-border bg-card p-5 fade-up" style={{ animationDelay: "120ms" }}><div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">Avg call score</div><div className="font-heading text-[28px] font-semibold mt-2">{data.avg_call_score}<span className="text-base text-muted-foreground">/10</span></div><div className="text-xs text-muted-foreground mt-1">Voice quality</div></div>
        <div className="rounded-lg border border-border bg-card p-5 fade-up" style={{ animationDelay: "180ms" }}><div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">Top channel</div><div className="font-heading text-[28px] font-semibold mt-2">{data.channels.sort((a, b) => b.revenue - a.revenue)[0].channel}</div><div className="text-xs text-muted-foreground mt-1">By attributed revenue</div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-lg border border-border bg-card p-5 fade-up">
          <h3 className="font-heading font-semibold mb-4">Revenue vs. cost (6 months)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.monthly} margin={{ left: -18, top: 6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#888" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#888" }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #eee", fontSize: 12 }} formatter={(v) => money(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue" fill="#EA580C" radius={[4, 4, 0, 0]} />
              <Bar dataKey="cost" fill="#E5E7EB" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-lg border border-border bg-card p-5 fade-up" style={{ animationDelay: "80ms" }}>
          <h3 className="font-heading font-semibold mb-2">Channel mix</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={data.channels} dataKey="revenue" nameKey="channel" innerRadius={46} outerRadius={72} paddingAngle={2}>
                {data.channels.map((_, i) => <Cell key={i} fill={CH[i % CH.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #eee", fontSize: 12 }} formatter={(v) => money(v)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {data.channels.map((c, i) => (
              <div key={c.channel} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: CH[i % CH.length] }} />{c.channel}</span>
                <span className="font-medium">{c.share}% · {money(c.revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-5 mt-4 fade-up">
        <h3 className="font-heading font-semibold mb-4">Guest engagement funnel</h3>
        <div className="grid grid-cols-4 gap-3">
          {data.funnel.map((f, i) => (
            <div key={f.stage} className="relative">
              <div className="rounded-md p-4 text-white" style={{ background: `linear-gradient(135deg, ${CH[0]}, #0F172A)`, opacity: 1 - i * 0.16 }}>
                <div className="font-heading text-2xl font-semibold">{f.value.toLocaleString()}</div>
                <div className="text-xs opacity-90 mt-0.5">{f.stage}</div>
              </div>
              {i > 0 && <div className="text-[11px] text-muted-foreground mt-1.5 text-center">{Math.round(f.value / data.funnel[i - 1].value * 100)}% conversion</div>}
            </div>
          ))}
        </div>
      </div>

      {data.voice && <VoicePerformance v={data.voice} />}
    </Page>
  );
}

function VoicePerformance({ v }) {
  const maxCount = Math.max(1, ...v.outcomes.map((o) => o.count));
  return (
    <div className="rounded-lg border border-border bg-card p-5 mt-4 fade-up" data-testid="voice-performance-card">
      <div className="flex items-center gap-2 mb-4">
        <span className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center"><PhoneCall size={16} weight="fill" /></span>
        <h3 className="font-heading font-semibold">Voice performance</h3>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-success/10 text-success border border-success/20 font-medium">Grok Voice</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <VStat icon={PhoneCall} label="Total calls" value={v.total_calls} testid="voice-stat-total" />
        <VStat icon={CheckCircle} label="Resolved rate" value={`${v.resolved_rate}%`} sub={`${v.resolved} resolved`} testid="voice-stat-resolved" />
        <VStat icon={Clock} label="Avg duration" value={fmtDur(v.avg_duration)} testid="voice-stat-duration" />
        <VStat icon={CurrencyDollar} label="Voice-sourced revenue" value={money(v.sourced_revenue)} sub={`${v.voice_contacts} voice contacts`} testid="voice-stat-revenue" />
      </div>

      <div className="mt-5">
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-foreground mb-3">Call outcomes</div>
        {v.outcomes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No calls logged yet.</p>
        ) : (
          <div className="space-y-2.5">
            {v.outcomes.map((o) => (
              <div key={o.key} className="flex items-center gap-3" data-testid={`voice-outcome-${o.key}`}>
                <span className="text-sm w-28 shrink-0 text-muted-foreground">{o.outcome}</span>
                <div className="flex-1 h-6 rounded-md bg-secondary overflow-hidden">
                  <div className="h-full rounded-md transition-all" style={{ width: `${(o.count / maxCount) * 100}%`, background: OUTCOME_COLOR[o.key] || "#CBD5E1" }} />
                </div>
                <span className="text-sm font-medium w-8 text-right">{o.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function VStat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-md border border-border p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground"><Icon size={15} /><span className="text-[11px] uppercase tracking-wide font-semibold">{label}</span></div>
      <div className="font-heading text-xl font-semibold mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
